import React from 'react';
import { Mail, Phone, MapPin, Clock, MessageCircle } from 'lucide-react';
import { hotelInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import DepartmentContact from './DepartmentContact';

const Contact = () => {
  const { t } = useLanguage();

  return (
    <section id="contact" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-900/20 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-400 font-medium tracking-widest text-sm uppercase">{t('contact.subtitle')}</span>
          <h2 className="text-4xl md:text-5xl font-serif text-white mt-3 mb-4">{t('contact.title')}</h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            {t('contact.description')}
          </p>
        </div>

        {/* Contact Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Address Card */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/30 transition-colors">
            <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
              <MapPin className="w-7 h-7 text-amber-400" />
            </div>
            <h4 className="text-white font-semibold mb-2 text-lg">{t('contact.address')}</h4>
            <p className="text-slate-400 text-sm leading-relaxed">{hotelInfo.address}</p>
          </div>

          {/* Phone Card */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/30 transition-colors">
            <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
              <Phone className="w-7 h-7 text-amber-400" />
            </div>
            <h4 className="text-white font-semibold mb-2 text-lg">{t('contact.phone')}</h4>
            <a 
              href={`tel:${hotelInfo.phone}`} 
              className="text-slate-400 hover:text-amber-400 transition-colors text-sm"
            >
              {hotelInfo.phone}
            </a>
          </div>

          {/* Email Card */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/30 transition-colors">
            <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
              <Mail className="w-7 h-7 text-amber-400" />
            </div>
            <h4 className="text-white font-semibold mb-2 text-lg">{t('contact.email')}</h4>
            <a 
              href={`mailto:${hotelInfo.email}`} 
              className="text-slate-400 hover:text-amber-400 transition-colors text-sm break-all"
            >
              {hotelInfo.email}
            </a>
          </div>

          {/* Hours Card */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/30 transition-colors">
            <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
              <Clock className="w-7 h-7 text-amber-400" />
            </div>
            <h4 className="text-white font-semibold mb-2 text-lg">{t('contact.receptionHours')}</h4>
            <p className="text-slate-400 text-sm">{t('contact.alwaysHere')}</p>
          </div>
        </div>

        {/* Department channels: Sales · Careers · Press */}
        <DepartmentContact />

        {/* WhatsApp / Direct Contact CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <a
              href={`https://wa.me/${hotelInfo.phone.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-medium transition-all hover:scale-105"
              data-testid="whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              {t('contact.whatsapp') || 'Chat on WhatsApp'}
            </a>
            <a
              href={`mailto:${hotelInfo.email}`}
              className="inline-flex items-center justify-center gap-3 bg-amber-700 hover:bg-amber-800 text-white px-8 py-4 rounded-xl font-medium transition-all hover:scale-105"
              data-testid="email-btn"
            >
              <Mail className="w-5 h-5" />
              {t('contact.sendEmail') || 'Send Email'}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
